<script setup lang="ts" generic="T">
// import { EventNames } from '@/types';
import { onClickOutside } from '@vueuse/core';
import { computed, nextTick, ref, watch } from 'vue';
import { ChqChangeEvent, type ObjectCallback, type ParamsCallback } from '../../utils/global-types';
import helpers from "../../utils/helpers";

const model = defineModel<any>()

// Props
const props = defineProps<{
    options: T[],
    name?: string,
    clearable?: boolean,
    allowCustom?: boolean,
    placeholder?: string,
    groupBy?: ((option: T) => string | number) | keyof T,
    disabled?: boolean,
    required?: boolean,
    menuPlacement?: 'top' | 'bottom',
    label?: ((option: T) => string | number) | keyof T,
    disableFiltering?: boolean,
    reduce?: (option: T) => any,
    searchBy?: (option: T) => string,
    onSearch?: ParamsCallback,
    onSelect?: ObjectCallback
}>()

// Default accessors
const getValue = props.reduce ?? ((o: any) => o)
const getLabel = (o: any) => {
    if (!props.label) {
        return o.label;
    }

    return typeof props.label === 'function' ? props.label(o) : o[props.label]
}

const getSearchText = props.searchBy ?? getLabel
const isOpen = ref(false);
const search = ref('');
const customList = ref([]);
const highlightedIndex = ref(-1);
const wrapper = ref<HTMLElement | null>(null);
const controlRef = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const optionRefs = ref<any[]>([]);

watch(search, () => {
    props.onSearch?.(search.value);

    if (props.allowCustom) {
        insertOption(search.value);
    }

    nextTick(() => {
        if (filteredOptions.value.length > 0) {
            highlightedIndex.value = 0;
        }
    })
});

watch(highlightedIndex, async (index) => {
    await nextTick()

    const el = optionRefs.value[index]
    if (el) {
        el.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth'
        })
    }
})

function insertOption(search: string) {
    const newList = (customList.value || []).slice();
    if (search && !newList.includes(search)) {
        newList.unshift(search);
    }
    customList.value = newList;
}

const menuPlacementClass = computed(() =>
    props.menuPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
)

const filteredOptions = computed(() => {
    if (props.disableFiltering) {
        return props.options;
    }

    const s = search.value.toLowerCase();
    return !props.allowCustom
        ? props.options.filter(opt => getSearchText(opt).toLowerCase().includes(s))
        : [
            ...props.options.filter(opt => getSearchText(opt).toLowerCase().includes(s)),
            ...customList.value.filter(opt => getSearchText(opt).toLowerCase().includes(s))
        ]
})

const groupedOptions = computed(() => {
    if (!props.groupBy) return { All: filteredOptions.value }
    const keyFn = typeof props.groupBy === 'function' ? props.groupBy : (o: T) => (o as any)[props.groupBy as any]
    return filteredOptions.value.reduce((groups: Record<string, T[]>, option: T) => {
        const key = String(keyFn(option));
        if (!groups[key]) groups[key] = []
        groups[key].push(option)
        return groups
    }, {})
})

const groupedOptionsWithFlat = computed(() => {
    let before = 0

    return Object.entries(groupedOptions.value).map(([groupName, items], gIndex) => {
        const rows = items.map((option, index) => ({
            option,
            index,
            gIndex,
            flat: before + index
        }))

        before += items.length
        return { groupName, gIndex, rows }
    })
})

const flatRows = computed(() => groupedOptionsWithFlat.value.flatMap(g => g.rows))

const selectedOption = computed(() => {
    if (props.reduce) {
        return props.options?.find(opt => getValue(opt) === model.value) ?? null;
    }

    return model.value;
})

const selectedLabel = computed(() =>
    selectedOption.value ? getLabel(selectedOption.value) : ''
)

const toggle = () => {
    if (props.disabled) {
        return;
    }

    isOpen.value = !isOpen.value
    if (isOpen.value) nextTick(() => searchInput.value?.focus())
}

const close = (returnFocus = false) => {
    isOpen.value = false
    search.value = ''
    highlightedIndex.value = -1

    if (props.allowCustom) {
        customList.value.length = 0;
    }

    if (returnFocus) {
        nextTick(() => controlRef.value?.focus())
    }
}

const select = (option: T) => {
    const val = getValue(option);
    model.value = val;
    props.onSelect?.(val);
    helpers.broadcastEvent(ChqChangeEvent);
    close();
}

const clear = function () {
    model.value = null;
    props.onSelect?.(null)
    helpers.broadcastEvent(ChqChangeEvent);
}

const moveDown = () => {
    if (highlightedIndex.value < flatRows.value.length - 1) {
        highlightedIndex.value++
    }
}

const moveUp = () => {
    if (highlightedIndex.value > 0) {
        highlightedIndex.value--
    }
}

const selectHighlighted = () => {
    const selected = flatRows.value[highlightedIndex.value];
    if (selected) select(selected.option)
}

onClickOutside(wrapper, () => close())

</script>
<template>
    <div class="relative w-full" ref="wrapper">
        <input type="text" class="hidden" v-if="props.required" :name="props.name" v-model="model" required>
        <!-- Control -->
        <div ref="controlRef" class="form-control px-3 py-2 flex justify-between items-center"
            :class="{ 'cursor-not-allowed': disabled, 'cursor-pointer': !disabled }" @click="toggle"
            :tabindex="disabled ? -1 : 0" @keydown.enter.prevent="toggle" @keydown.space.prevent="toggle"
            @keydown.down.prevent="toggle">
            <!-- Selected value slot -->
            <span>
                <template v-if="model">
                    <span :class="{ 'text-muted': disabled }">
                        <slot name="selected" :option="selectedOption" :model="model">
                            <span :class="{ 'text-muted': !model }">{{ selectedLabel }}</span>
                        </slot>
                    </span>
                </template>
                <template v-else>
                    <span :class="{ 'text-muted': !model }">{{ placeholder || 'Select an option' }}</span>
                </template>
            </span>
            <div class="space-x-2">
                <span v-if="clearable && model" class="text-gray-500 z-50">
                    <i class="fa-solid fa-xmark" @click.stop="clear"></i>
                </span>
                <span class="text-gray-500"><i class="fa-solid fa-chevron-down"></i></span>
            </div>
        </div>

        <!-- Dropdown list -->
        <div v-if="isOpen" class="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow z-50"
            :class="menuPlacementClass" @keydown.esc="close(true)">
            <!-- Search input -->
            <input v-model="search" ref="searchInput" tabindex="0"
                class="form-control w-full border-b px-3 py-2 rounded-none focus:outline-none" type="text"
                placeholder="Search..." @keydown.down.prevent="moveDown" @keydown.up.prevent="moveUp"
                @keydown.enter.prevent="selectHighlighted" />

            <div class="max-h-60 overflow-auto">
                <template v-for="items in groupedOptionsWithFlat" :key="items.gIndex">
                    <div class="px-3 py-1 text-sm font-semibold text-gray-500 bg-gray-50 sticky top-0" v-if="groupBy">
                        {{ items.groupName }}
                    </div>
                    <ul>
                        <li v-for="row in items.rows" :key="row.flat" class="px-3 py-2 cursor-pointer"
                            :class="{ 'bg-blue-100': row.flat === highlightedIndex }" @click="select(row.option)"
                            @mouseover="highlightedIndex = row.flat" :ref="el => optionRefs[row.flat] = el">
                            <slot name="option" :option="row.option" :search="search">
                                <span v-html="helpers.highlight(getLabel(row.option), search)" />
                            </slot>
                        </li>
                    </ul>
                </template>

                <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-gray-400">
                    No results
                </div>
            </div>
        </div>
    </div>
</template>